let baseTitle = '';
let currentCount = 0;

let pulseTimer = null;

let originalFaviconHref = null;
let dotFaviconHref = null;

// Track tab visibility so we can stop flashing when user returns.
let tabVisible = !document.hidden;
document.addEventListener('visibilitychange', () => {
  tabVisible = !document.hidden;
  if (tabVisible) {
    // User switched back — stop flashing, restore stable state.
    stopPulse();
    document.title = stableTitle();
    if (currentCount > 0) setFavicon(dotFaviconHref, false);
    else setFavicon(originalFaviconHref, false);
  }
});

function ensureFavicons() {
  const links = Array.from(document.querySelectorAll('link[rel~="icon"]'));
  if (!links || links.length === 0) {
    const l = document.createElement('link');
    l.rel = 'icon';
    l.href = '/favicon.ico';
    document.head.appendChild(l);
    return ensureFavicons();
  }
  if (!originalFaviconHref) {
    originalFaviconHref = links[0].getAttribute('href') || '/favicon.ico';
  }
  if (!dotFaviconHref) {
    const c = document.createElement('canvas');
    c.width = 32;
    c.height = 32;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, 32, 32);
    // Orange dot in bottom-right corner
    ctx.fillStyle = '#f97316';
    ctx.beginPath();
    ctx.arc(24, 24, 7, 0, Math.PI * 2);
    ctx.fill();
    dotFaviconHref = c.toDataURL('image/png');
  }
}

function stableTitle() {
  if (currentCount > 0) return `(${currentCount > 99 ? '99+' : currentCount}) ${baseTitle}`;
  return baseTitle;
}

function setFavicon(href, bust) {
  const links = Array.from(document.querySelectorAll('link[rel~="icon"]'));
  const target = bust && href && !String(href).startsWith('data:')
    ? `${href}${String(href).indexOf('?') === -1 ? '?' : '&'}v=${Date.now()}`
    : href;
  for (const l of links) {
    l.setAttribute('href', target);
  }
}

function stopPulse() {
  if (pulseTimer) clearInterval(pulseTimer);
  pulseTimer = null;
}

function startPulse() {
  ensureFavicons();
  if (pulseTimer) return; // already flashing
  let on = false;
  pulseTimer = setInterval(() => {
    // Stop when tab becomes visible again (handled by visibilitychange too, but be safe).
    if (tabVisible) {
      stopPulse();
      document.title = stableTitle();
      if (currentCount > 0) setFavicon(dotFaviconHref, false);
      else setFavicon(originalFaviconHref, false);
      return;
    }
    on = !on;
    setFavicon(on ? dotFaviconHref : originalFaviconHref, true);
    const st = stableTitle();
    document.title = on ? `🔔 ${st}` : st;
  }, 500);
}

// ── Notification API ──
let notifPermission = typeof window.Notification !== 'undefined' ? window.Notification.permission : 'denied';

export async function requestNotificationPermission() {
  if (typeof window.Notification === 'undefined') return 'denied';
  if (window.Notification.permission === 'granted') return 'granted';
  try {
    const p = await window.Notification.requestPermission();
    notifPermission = p;
    return p;
  } catch (e) {
    return 'denied';
  }
}

/**
 * Send a browser-native notification. On Windows this flashes the taskbar icon.
 * Only fires when the tab is NOT focused.
 * @param {string} title
 * @param {string} body
 * @param {string} [route] - optional route to navigate on click
 * @param {string} [category] - tag category to avoid collapsing different types
 * @param {boolean} [persistent] - if true, notification won't auto-close (default: false)
 * @param {Function} [onClickCallback] - optional callback to execute before navigation
 */
export function sendNativeNotification(title, body, route, category, persistent = false, onClickCallback = null) {
  const perm = typeof window.Notification !== 'undefined' ? window.Notification.permission : 'denied';
  if (perm !== 'granted') return;
  try {
    const n = new window.Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: category ? `gondgan-${category}` : 'gondgan-alert',
      renotify: true,
      requireInteraction: persistent  // Keep notification visible until user interacts
    });
    n.onclick = async (event) => {
      event.preventDefault();

      // Try to focus the existing window/tab instead of opening a new one
      try {
        if (window.parent && window.parent !== window) {
          window.parent.focus();
        } else {
          window.focus();
        }
      } catch (e) {
        window.focus();
      }

      // Execute callback first (e.g., logout)
      if (onClickCallback && typeof onClickCallback === 'function') {
        try {
          await onClickCallback();
        } catch (e) {}
      }

      // Then navigate
      if (route) {
        const { default: router } = require('../router');
        if (router.currentRoute.path !== route) {
          router.replace(route).catch(() => {});
        }
      }
      n.close();
    };
    // Auto-close after 6s to avoid notification pile-up (unless persistent)
    if (!persistent) {
      setTimeout(() => { try { n.close(); } catch (e) {} }, 6000);
    }
  } catch (e) {}
}

export function updateAttention(nextCount, nextBaseTitle) {
  ensureFavicons();

  const n = Number(nextCount || 0);
  const bt = nextBaseTitle || baseTitle || document.title || '';
  const countIncreased = n > currentCount;

  baseTitle = bt;
  currentCount = n;

  document.title = stableTitle();

  // Native app badge (PWA).
  try {
    if (navigator && typeof navigator.setAppBadge === 'function' && typeof navigator.clearAppBadge === 'function') {
      if (currentCount > 0) navigator.setAppBadge(Math.min(currentCount, 99));
      else navigator.clearAppBadge();
    }
  } catch (e) {}

  if (currentCount > 0) {
    setFavicon(dotFaviconHref, true);
    // Flash continuously when tab is hidden and count increased.
    if (countIncreased && !tabVisible) startPulse();
  } else {
    stopPulse();
    setFavicon(originalFaviconHref, true);
  }
}
